// Module-level (not per-instance) state for the AJAX product swap. It has
// to live outside the class: <cross-sell-strip> is itself a descendant of
// the section it swaps, so a successful swap destroys and recreates the
// very instance that started it — anything tracked only on `this` would be
// lost the moment that happens. Keeping it here also lets a single
// `popstate` listener (registered once, below) handle the browser's Back
// button correctly regardless of which instance happens to exist at that
// moment.
// activeInstance (not just a section id string) is what popstate replays
// against — a quick-add/quick-view popup can render this same section a
// second time on top of the page it was opened from, and Shopify reuses
// that section's id verbatim in both copies. A plain id-string lookup would
// then always resolve to whichever copy comes first in the DOM, silently
// updating the wrong (often invisible) one. Each instance instead caches
// its OWN ancestor via closest() at connect time, which is collision-proof
// regardless of duplicate ids elsewhere in the document.
let activeInstance = null;
let productModelSrc = null;
let currentUrl = window.location.href;

// Swaps only ever run one at a time. Selecting again while a fetch is
// still in flight doesn't start a second, overlapping one — it just
// records the newest choice in pendingUrl, and the loop below picks that
// up as soon as the current fetch finishes. This trades a small amount of
// "instant cancel" responsiveness for never having two fetches racing to
// both touch the same section/loading-class state at once.
let swapping = false;
let pendingUrl = null;
let pendingPushState = true;

async function swapSectionContent(instance, url, pushState) {
  if (!instance || url === currentUrl) return;
  pendingUrl = url;
  pendingPushState = pushState;
  if (swapping) return;
  swapping = true;

  const sectionWrapper = instance.sectionWrapper;
  if (!sectionWrapper || !sectionWrapper.isConnected) {
    swapping = false;
    return;
  }

  try {
    sectionWrapper.classList.add('cross-sell-strip-swapping');

    while (pendingUrl && pendingUrl !== currentUrl) {
      const targetUrl = pendingUrl;
      const targetPushState = pendingPushState;

      try {
        // Fetching the plain product URL (no ?section_id=) and pulling
        // <product-info> back out of the full response — rather than
        // Section Rendering API's ?section_id= — is deliberate: this same
        // component can end up rendered a second time inside the theme's
        // own quick-add-modal (assets/quick-add.js), which fetches THIS
        // exact way. A section id is only ever valid on the page it was
        // rendered on; the quick-add modal's happens to be scoped to the
        // one product it was opened for, and 404s if reused against a
        // different product's URL. Matching quick-add.js's approach avoids
        // that entirely — <product-info> is present on every product page,
        // full page fetch or not, so this works identically on the real
        // page and inside the modal.
        const response = await fetch(targetUrl);

        if (response.ok) {
          const html = await response.text();
          const newDocument = new DOMParser().parseFromString(html, 'text/html');
          const newSectionWrapper = newDocument.querySelector('product-info');

          // Both branches below used to fail completely silently — no
          // error, no visible change, nothing in the console — making a
          // real failure (wrong URL, blocked request, a template without
          // a <product-info> wrapper, etc.) indistinguishable from "it's
          // just not updating for some reason". Logging makes an actual
          // problem here diagnosable instead of invisible.
          if (!newSectionWrapper) {
            console.error(
              `[cross-sell-strip] Fetched ${targetUrl} successfully, but its HTML has no <product-info> element ` +
              'to swap in — the product page did not update. This can happen if the product uses a template ' +
              "without the standard product section, or if the fetched page wasn't actually the product page " +
              '(e.g. a password/interstitial page).'
            );
          }

          // Something else on the page — most notably product-info.js's own
          // variant-driven product swap — can replace this same wrapper's
          // containing page context while this fetch was in flight. Writing
          // into a node no longer part of the live document would be a
          // silent, hard-to-debug no-op at best, so skip it if so.
          if (newSectionWrapper && sectionWrapper.isConnected) {
            sectionWrapper.innerHTML = newSectionWrapper.innerHTML;
            currentUrl = targetUrl;

            // magnify.js (zoom-on-hover) only ever runs once, at initial
            // page load, attaching onclick handlers directly to whichever
            // images existed at that moment — it never re-scans on its
            // own. Re-running it (safe: a plain global function, not a
            // one-time side effect) picks up the freshly-swapped-in
            // images. It's simply undefined if zoom-on-hover isn't
            // enabled for this section, so this is a no-op in that case.
            if (typeof window.enableZoomOnHover === 'function') window.enableZoomOnHover(2);

            // <product-model> only upgrades into a working 3D viewer once
            // product-model.js has run at least once to register it —
            // which never happened if the originally-loaded product had
            // no 3D media. The <script src="product-model.js"> tag inside
            // the freshly-fetched HTML won't fix that: scripts inserted
            // via innerHTML never execute. Load it ourselves via a real
            // <script> element, which does.
            if (
              sectionWrapper.querySelector('product-model') &&
              !customElements.get('product-model') &&
              productModelSrc
            ) {
              const script = document.createElement('script');
              script.src = productModelSrc;
              document.body.appendChild(script);
            }

            if (targetPushState) window.history.pushState({}, '', targetUrl);
          }
        } else {
          console.error(`[cross-sell-strip] Fetching ${targetUrl} failed with status ${response.status} — the product page did not update.`);
        }
      } catch (error) {
        console.error('[cross-sell-strip] Fetch threw an error — the product page did not update.', error);
      }

      // Only clear pendingUrl if nothing newer came in while this fetch was
      // running — otherwise loop again immediately for that newer choice.
      if (pendingUrl === targetUrl) pendingUrl = null;
    }
  } finally {
    swapping = false;
    sectionWrapper.classList.remove('cross-sell-strip-swapping');
  }
}

window.addEventListener('popstate', () => {
  // Without this, pressing Back changes the URL (the browser does that
  // natively regardless) but the page silently keeps showing whatever was
  // last swapped in — Shopify's own routing never re-engages for a
  // pushState-only navigation, so nothing else re-fetches the content.
  currentUrl = null; // the URL we're going back to was already "current" once; allow re-fetching it
  if (activeInstance && activeInstance.isConnected) {
    swapSectionContent(activeInstance, window.location.href, false);
  }
});

if (!customElements.get('cross-sell-strip')) {
  customElements.define(
    'cross-sell-strip',
    class CrossSellStrip extends HTMLElement {
      constructor() {
        super();

        this.scroller = this.querySelector('[data-thumbs]');
        this.step = 0;
        this.selectedItem = null;
        this.sectionId = this.dataset.sectionId || null;
        this.updateBreakoutWidth = this.updateBreakoutWidth.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        if (!this.scroller) return;

        this.sectionWrapper =
          this.closest('product-info') ||
          this.closest('[id^="shopify-section-"]') ||
          (this.sectionId ? document.getElementById('shopify-section-' + this.sectionId) : null);
        activeInstance = this;
        if (this.dataset.productModelSrc) productModelSrc = this.dataset.productModelSrc;

        // Break out to the viewport's right edge BEFORE reading clientWidth
        // below — copiesPerSide is sized off that width, so measuring it
        // pre-breakout would under-buffer the clone list for the actual
        // (much wider) drag area, running out of clones/showing empty space
        // near either end long before a real drag could reach it.
        this.updateBreakoutWidth();
        window.addEventListener('resize', this.onResize);

        const templateItems = Array.from(this.scroller.querySelectorAll('.cross-sell-strip__thumb-item'));
        this.setCount = templateItems.length;
        if (this.setCount < 2) return;

        const templateStep = templateItems[1].offsetLeft - templateItems[0].offsetLeft;
        if (!templateStep) return;
        const approxSetWidth = templateStep * this.setCount;

        // Clone the real list into enough repeated copies to scroll/drag
        // into on either side — at least a full viewport-width's worth of
        // buffer, more if products are few and the real list is narrow.
        // Sized off screen.width (not just the current window/clientWidth)
        // so a later resize/maximize within the same screen — the common
        // case — never runs short of clones without needing a full rebuild.
        // Only ever the ONE designated "middle" copy carries the lit dot;
        // every other copy is aria-hidden, tab-index -1 scroll buffer.
        const viewportWidth = Math.max(this.scroller.clientWidth || 0, window.screen.width || 0) || approxSetWidth;
        this.copiesPerSide = Math.max(1, Math.ceil(viewportWidth / approxSetWidth)) + 1;
        const totalCopies = this.copiesPerSide * 2 + 1;

        this.scroller.textContent = '';
        this.allItems = [];
        for (let copy = 0; copy < totalCopies; copy++) {
          templateItems.forEach((templateItem) => {
            const clone = templateItem.cloneNode(true);
            if (copy !== this.copiesPerSide) {
              const dot = clone.querySelector('.cross-sell-strip__thumb-dot');
              if (dot) dot.classList.remove('cross-sell-strip__thumb-dot--active');
              clone.setAttribute('aria-hidden', 'true');
              const link = clone.querySelector('a');
              if (link) link.setAttribute('tabindex', '-1');
            }
            this.scroller.appendChild(clone);
            this.allItems.push(clone);
          });
        }

        // "Anchor" position: how far the 2nd item's left edge sits from the
        // 1st item's left edge, i.e. one item's width plus the gap between
        // them — used below to detect whichever item is currently sitting
        // in that 2nd slot as the strip scrolls/drags. Measured from the
        // real, now-laid-out clones rather than assumed, to avoid rounding
        // drift accumulating over many copies.
        this.step = this.allItems[1].offsetLeft - this.allItems[0].offsetLeft;
        this.singleSetWidth = this.allItems[this.setCount].offsetLeft - this.allItems[0].offsetLeft;

        const middleFirst = this.allItems[this.copiesPerSide * this.setCount];

        // The one designated middle copy's current-product item is the one
        // still carrying the dot lit server-side — track that exact
        // element so the first selectItem() call recognizes it as already
        // selected instead of re-deciding. Scoped to the middle copy's own
        // range (rather than .find() over every clone) — now that the
        // strip renders products in a fixed collection order instead of
        // always forcing the current one into a fixed slot, the current
        // product can land at ANY index within a set, so a plain .find()
        // could just as easily match a different copy of the same product
        // elsewhere in the (deliberately repeated) clone list.
        this.selectedItem =
          this.allItems
            .slice(this.copiesPerSide * this.setCount, this.copiesPerSide * this.setCount + this.setCount)
            .find((item) => item.querySelector('.cross-sell-strip__thumb-dot--active')) || null;

        // Start scrolled so the current product sits exactly in the anchor
        // slot — wherever it actually falls within the set, since it's no
        // longer guaranteed to be the 2nd item (see above).
        const previousScrollBehavior = this.scroller.style.scrollBehavior;
        this.scroller.style.scrollBehavior = 'auto';
        this.scroller.scrollLeft = this.selectedItem ? this.selectedItem.offsetLeft - this.step : middleFirst.offsetLeft;
        this.scroller.style.scrollBehavior = previousScrollBehavior;

        this.onScroll = this.onScroll.bind(this);
        // Deferred one frame: setting scrollLeft just above fires its own
        // native 'scroll' event asynchronously, and attaching this listener
        // synchronously (as before) is still in time to catch it — treating
        // that purely-programmatic initial-positioning scroll exactly like
        // a real user scroll. Normally harmless (it just re-confirms
        // whatever selectedItem already was), but when the current product
        // isn't found in this render of the strip at all (selectedItem is
        // null — e.g. it fell outside a collection-size limit upstream in
        // cross-sell-strip.liquid), that "settle" resolves to whatever
        // random item geometrically landed in the anchor slot and — since
        // null !== that item — silently swaps the page over to IT via
        // selectItem()'s swapSectionContent() call, a few seconds after
        // load, with zero user interaction. Waiting a frame lets that one
        // synthetic scroll event fire and dispatch to no listener before
        // this attaches, so onScroll only ever sees scrolls a real drag,
        // wheel, or touch gesture caused.
        requestAnimationFrame(() => {
          this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
        });

        // Native scroll alone only responds to a trackpad/wheel or an
        // actual scrollbar (hidden here) — there's no way to click-and-drag
        // the strip itself with a mouse. Add that as a thin layer on top:
        // dragging just moves scrollLeft directly.
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.scroller.addEventListener('pointerdown', this.onPointerDown);

        // Images and links are natively draggable in every browser — that's
        // the browser's own "drag this image out" feature (the ghost image
        // preview), separate from and fighting our own pointer-based drag
        // above. Cancelling dragstart turns that native behavior off.
        this.onDragStart = (event) => event.preventDefault();
        this.scroller.addEventListener('dragstart', this.onDragStart);

        // A tap/click selects that item instead of following the link —
        // the strip is a picker, not a set of direct navigation links.
        this.onClick = this.onClick.bind(this);
        this.scroller.addEventListener('click', this.onClick);

        this.updatePreviewNearAnchor();
      }

      disconnectedCallback() {
        if (this.scroller && this.onScroll) {
          this.scroller.removeEventListener('scroll', this.onScroll);
        }
        if (this.scroller && this.onPointerDown) {
          this.scroller.removeEventListener('pointerdown', this.onPointerDown);
        }
        if (this.scroller && this.onClick) {
          this.scroller.removeEventListener('click', this.onClick);
        }
        if (this.scroller && this.onDragStart) {
          this.scroller.removeEventListener('dragstart', this.onDragStart);
        }
        if (this.onResize) {
          window.removeEventListener('resize', this.onResize);
        }
        clearTimeout(this.settleTimeout);
        clearTimeout(this.resizeTimeout);
      }

      updateBreakoutWidth() {
        // Extends the strip from wherever it actually sits (inside the
        // narrower product-info column) out to the real right edge of the
        // viewport, regardless of any parent width/padding — measuring the
        // strip's own live position rather than trying to reverse-engineer
        // it from page-width/grid CSS variables, which vary by breakpoint
        // and container context (see the removed margin/padding-based
        // attempt this replaced, in component-cross-sell-strip.css).
        // document.documentElement.clientWidth, not window.innerWidth —
        // innerWidth includes the vertical scrollbar's own width, so
        // whenever a vertical scrollbar is actually present, sizing off
        // innerWidth put this element's right edge scrollbar-width past
        // the page's real content edge (clientWidth), which is exactly
        // enough overflow to trigger a second, page-wide horizontal
        // scrollbar. clientWidth already excludes it.
        const left = this.scroller.getBoundingClientRect().left;
        this.scroller.style.width = `${document.documentElement.clientWidth - left}px`;
      }

      onResize() {
        // Debounced — a window drag-resize fires this dozens of times a
        // second, and re-reading getBoundingClientRect() on every single
        // one forces a synchronous layout each time for no visible benefit
        // over settling once the resize has actually paused.
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(this.updateBreakoutWidth, 100);
      }

      onClick(event) {
        // Suppress the underlying <a href> — selecting is the whole point
        // of clicking here, not navigating away from the page.
        event.preventDefault();
        const item = event.target.closest('.cross-sell-strip__thumb-item');
        if (item) this.selectItem(item);
      }

      onPointerDown(event) {
        if (event.button !== undefined && event.button !== 0) return;
        // Touch already gets smooth, momentum-based scrolling for free from
        // the OS/browser natively — this pointer-drag path exists only to
        // let a MOUSE click-and-drag the strip (there's no scrollbar, and
        // native scroll alone doesn't respond to a mouse drag). Routing
        // touch through it too meant every swipe set scrollLeft directly
        // on each pointermove with no momentum/deceleration once the
        // finger lifted — an abrupt stop instead of the smooth native
        // glide touch scrolling normally has. See touch-action in
        // component-cross-sell-strip.css, which now lets touch scroll
        // natively instead of routing through this at all.
        if (event.pointerType === 'touch') return;
        this.dragging = true;
        this.dragMoved = false;
        this.dragStartX = event.clientX;
        this.dragStartScrollLeft = this.scroller.scrollLeft;
        // Dragging sets scrollLeft directly every frame, so a smooth-scroll
        // transition here would fight it and feel laggy — switch to auto
        // for the duration of the drag, then restore smooth afterward for
        // any click/keyboard-triggered scrolling.
        this.previousScrollBehavior = this.scroller.style.scrollBehavior;
        this.scroller.style.scrollBehavior = 'auto';
        this.scroller.classList.add('is-dragging');
        if (this.scroller.setPointerCapture) {
          try {
            this.scroller.setPointerCapture(event.pointerId);
          } catch (error) {
            // Pointer capture can fail silently on some browsers; dragging
            // still works via the listeners below.
          }
        }
        this.scroller.addEventListener('pointermove', this.onPointerMove);
        this.scroller.addEventListener('pointerup', this.onPointerUp);
        this.scroller.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.dragging) return;
        const dx = event.clientX - this.dragStartX;
        if (Math.abs(dx) > 4) this.dragMoved = true;
        // A slightly-less-than-1:1 ratio makes the drag feel a touch slower
        // and more deliberate than a raw 1:1 follow, rather than snappy.
        this.scroller.scrollLeft = this.dragStartScrollLeft - dx * 0.85;
      }

      onPointerUp() {
        if (!this.dragging) return;
        this.dragging = false;
        this.scroller.classList.remove('is-dragging');
        this.scroller.style.scrollBehavior = this.previousScrollBehavior;
        this.scroller.removeEventListener('pointermove', this.onPointerMove);
        this.scroller.removeEventListener('pointerup', this.onPointerUp);
        this.scroller.removeEventListener('pointercancel', this.onPointerUp);

        // Select immediately on release rather than waiting on the scroll
        // debounce below — this is the moment the user's choice is actually
        // made, and doesn't depend on whether a 'scroll' event fires again
        // afterward (e.g. if the drag ended exactly on a snap point, with
        // nothing left to scroll, no further 'scroll' event happens at all).
        clearTimeout(this.settleTimeout);
        this.selectClosestToAnchor();
        this.updatePreviewNearAnchor();

        // Re-centering back toward the middle copy only ever happens here,
        // once the drag has actually stopped — never mid-drag. That's the
        // fix for the glitch this used to have: correcting scrollLeft
        // *while* onPointerMove is also setting scrollLeft from a stale
        // dragStartScrollLeft baseline made the two fight each other every
        // frame. Now there's nothing to fight — this only runs once
        // dragging is already false.
        this.recenterIfNeeded();

        if (this.dragMoved) {
          this.scroller.addEventListener(
            'click',
            (clickEvent) => {
              clickEvent.preventDefault();
              clickEvent.stopPropagation();
            },
            { capture: true, once: true }
          );
        }
      }

      onScroll() {
        // The scale preview tracks every scroll tick live — it's cheap (a
        // class toggle) and is the whole point of a live "you'll get this
        // one if you stop here" cue, unlike the debounced selection below.
        this.updatePreviewNearAnchor();

        // Whatever settles into the 2nd slot — after a drag, a wheel
        // scroll, or a swipe — becomes the selected item, the same as a
        // direct click does. "Settled" here just means scroll position
        // hasn't moved for a short pause; debouncing avoids reading a
        // mid-motion position while it's still sliding past. This is also
        // where wheel/trackpad scrolling (as opposed to a drag, which
        // re-centers on pointer-up instead) gets a chance to re-center —
        // by the time this fires, scrolling has already stopped, so it's
        // just as safe as the pointer-up case.
        clearTimeout(this.settleTimeout);
        this.settleTimeout = setTimeout(() => {
          this.selectClosestToAnchor();
          this.recenterIfNeeded();
        }, 120);
      }

      recenterIfNeeded() {
        // Never correct mid-drag (this.dragging would be true) — see the
        // comment in onPointerUp for why. Every other caller only runs
        // once scrolling has genuinely stopped, so this is always safe.
        if (this.dragging) return;
        const scrollLeft = this.scroller.scrollLeft;
        const middleStart = this.copiesPerSide * this.singleSetWidth;
        const setsAway = Math.round((scrollLeft - middleStart) / this.singleSetWidth);
        if (!setsAway) return;
        const previousScrollBehavior = this.scroller.style.scrollBehavior;
        this.scroller.style.scrollBehavior = 'auto';
        this.scroller.scrollLeft = scrollLeft - setsAway * this.singleSetWidth;
        this.scroller.style.scrollBehavior = previousScrollBehavior;

        // The jump lands on a different (but visually identical, same
        // on-screen position) clone than the one selectItem/updatePreview
        // just marked — re-resolve against the clone that's actually there
        // now, or the dot/scale would appear to detach from the anchor
        // slot the instant this fires.
        this.selectClosestToAnchor();
        this.updatePreviewNearAnchor();
      }

      findClosestToAnchor() {
        const anchorLeft = this.step;
        const scrollLeft = this.scroller.scrollLeft;
        let closest = null;
        let closestDistance = Infinity;
        this.allItems.forEach((item) => {
          const viewportLeft = item.offsetLeft - scrollLeft;
          const distance = Math.abs(viewportLeft - anchorLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = item;
          }
        });
        return closest;
      }

      selectClosestToAnchor() {
        const closest = this.findClosestToAnchor();
        if (closest) this.selectItem(closest);
      }

      updatePreviewNearAnchor() {
        const closest = this.findClosestToAnchor();
        if (!closest || closest === this.nearAnchorItem) return;
        if (this.nearAnchorItem) {
          this.nearAnchorItem.classList.remove('cross-sell-strip__thumb-item--near-anchor');
        }
        closest.classList.add('cross-sell-strip__thumb-item--near-anchor');
        this.nearAnchorItem = closest;
      }

      selectItem(item) {
        if (!item || item === this.selectedItem) return;

        const previousHandle = this.selectedItem ? this.selectedItem.dataset.handle : null;
        const newHandle = item.dataset.handle;
        this.allItems.forEach((otherItem) => {
          if (otherItem.dataset.handle !== previousHandle && otherItem.dataset.handle !== newHandle) return;
          const dot = otherItem.querySelector('.cross-sell-strip__thumb-dot');
          if (!dot) return;
          dot.classList.toggle('cross-sell-strip__thumb-dot--active', otherItem.dataset.handle === newHandle);
        });
        this.selectedItem = item;

        const previousScrollBehavior = this.scroller.style.scrollBehavior;
        this.scroller.style.scrollBehavior = 'auto';
        this.scroller.scrollLeft = item.offsetLeft - this.step;
        this.scroller.style.scrollBehavior = previousScrollBehavior;

        // The current product's own item has no <a> (nothing to swap to);
        // every other item links to the product it represents.
        const link = item.querySelector('a');
        if (link) swapSectionContent(this, link.href, true);
      }
    }
  );
}
